import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Building2,
  Users,
  Plus,
  ChevronRight,
  ChevronDown,
  ClipboardList,
  Briefcase,
  MessageSquare,
  UserX,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { departmentsApi } from '../../lib/api/departmentsApi';
import { usersApi } from '../../lib/api/usersApi';
import { hasPermission } from '../../lib/permissions';
import DepartmentDialog from './DepartmentDialog';
import UserDialog from './UserDialog';
import ConfirmModal from '../../components/ui/ConfirmModal';

/** Relative label for session lastActiveAt (matches Profile session copy). */
function formatLastActive(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString();
}

/** Most recently active team member excluding the viewer (org admin row is always self; no redundant “last seen”). */
function getLastActiveTeamMemberSummary(teamMemberUsers) {
  const candidates = [];
  (teamMemberUsers || []).forEach((u) => {
    if (u.lastActiveAt) {
      candidates.push({
        name: u.name || u.email || 'Member',
        lastActiveAt: new Date(u.lastActiveAt),
      });
    }
  });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.lastActiveAt - a.lastActiveAt);
  const top = candidates[0];
  const relative = formatLastActive(top.lastActiveAt);
  if (!relative) return null;
  return { name: top.name, relative };
}

const OrganizationDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamUsers, setTeamUsers] = useState({});
  const [selectedTeamIdForUser, setSelectedTeamIdForUser] = useState(null);
  const [newTeamAdminFlow, setNewTeamAdminFlow] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'
  const [searchTerm, setSearchTerm] = useState('');
  const tabsRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  // Department admin: members view (same UI pattern as teams)
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberActiveTab, setMemberActiveTab] = useState('active');
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const memberTabsRef = useRef(null);
  const [memberIndicator, setMemberIndicator] = useState({ left: 0, width: 0 });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    onConfirm: null,
  });
  const orgName = user?.company || user?.name || 'Your Organisation';
  const createdDate = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  const isOrgAdmin = user?.accountType === 'org' && user?.role === 'admin';
  const isDepartmentAdmin = user?.role === 'department_admin' || user?.role === 'dept_admin';

  const fetchStats = () => {
    if (!isOrgAdmin) return;
    setLoading(true);
    departmentsApi
      .getOrgDashboard()
      .then((data) => setDashboard(data))
      .catch(() => setDashboard(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOrgAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    departmentsApi
      .getOrgDashboard()
      .then((data) => setDashboard(data))
      .catch(() => setDashboard(null))
      .finally(() => setLoading(false));
  }, [user?.accountType, user?.role]);

  const refreshTeamsAndUsers = useCallback(async () => {
    if (!isOrgAdmin) return;
    try {
      const [deptRes, usersRes] = await Promise.all([departmentsApi.list(), usersApi.list()]);
      const deptList = deptRes.departments || [];
      const usersList = usersRes.users || [];
      const map = {};
      deptList.forEach((d) => {
        const id = d._id?.toString() || '';
        if (id) map[id] = [];
      });
      usersList.forEach((u) => {
        const depts = u.departmentIds || [];
        depts.forEach((d) => {
          const id = (d._id || d).toString();
          if (!map[id]) map[id] = [];
          map[id].push(u);
        });
      });
      setTeams(deptList);
      setTeamUsers(map);
    } catch (err) {
      console.error('Error loading organization teams/users:', err);
    }
  }, [isOrgAdmin]);

  // Load teams (departments) and users for simple per-team view
  useEffect(() => {
    if (!isOrgAdmin) return;
    refreshTeamsAndUsers();
  }, [isOrgAdmin, refreshTeamsAndUsers]);

  // Load members for department admin (backend typically returns only their department's users)
  useEffect(() => {
    if (!isDepartmentAdmin) return;
    let cancelled = false;
    setMembersLoading(true);
    usersApi
      .list()
      .then((data) => {
        if (!cancelled) setMembers(data.users || []);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isDepartmentAdmin]);

  const stats = dashboard || {
    departments: 0,
    users: 0,
    assessments: 0,
    jobPostings: 0,
    interviews: 0,
    usersByDepartment: [],
  };
  const canViewAssessments = hasPermission(user, 'assessment.view');
  const canViewAIInterviews = hasPermission(user, 'interview.view');

  const metricCards = [
    {
      label: 'Teams',
      value: stats.departments,
      icon: Building2,
      href: '/dashboard/organization/teams',
      iconBg: 'bg-blue-100',
      text: 'text-blue-700',
    },
    {
      label: 'Users',
      value: stats.users,
      icon: Users,
      href: '/dashboard/organization/users',
      iconBg: 'bg-violet-100',
      text: 'text-violet-700',
    },
    ...(canViewAssessments
      ? [
          {
            label: 'Assessments',
            value: stats.assessments,
            icon: ClipboardList,
            href: '/dashboard/library/assessments',
            iconBg: 'bg-emerald-100',
            text: 'text-emerald-700',
          },
        ]
      : []),
    {
      label: 'Job Postings',
      value: stats.jobPostings,
      icon: Briefcase,
      href: '/dashboard/job-post',
      iconBg: 'bg-amber-100',
      text: 'text-amber-700',
    },
    ...(canViewAIInterviews
      ? [
          {
            label: 'AI Interviews',
            value: stats.interviews,
            icon: MessageSquare,
            href: '/dashboard/library/ai-interviews',
            iconBg: 'bg-indigo-100',
            text: 'text-indigo-700',
          },
        ]
      : []),
  ];

  const activeTeamsCount = teams.filter((t) => (t.status || 'active') === 'active').length;
  const inactiveTeamsCount = teams.filter((t) => t.status === 'inactive').length;

  const tabsList = [
    { id: 'active', label: 'Active Teams', count: activeTeamsCount },
    { id: 'inactive', label: 'Inactive Teams', count: inactiveTeamsCount },
  ];

  // Measure active tab for sliding underline
  useLayoutEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const tabEl = container.querySelector(`[data-tab-id="${activeTab}"]`);
    if (!tabEl) return;
    const measure = () => {
      const left = tabEl.offsetLeft;
      const width = tabEl.offsetWidth;
      setIndicator((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
    };
    measure();
    // Re-measure after layout (tabs appear after loading, so layout can be delayed)
    const t1 = setTimeout(measure, 0);
    const t2 = setTimeout(measure, 50);
    const rafId = requestAnimationFrame(() => {
      measure();
      requestAnimationFrame(measure);
    });
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [activeTab]);

  const filteredTeams = useMemo(() => {
    const byStatus =
      activeTab === 'active'
        ? teams.filter((t) => (t.status || 'active') === 'active')
        : teams.filter((t) => t.status === 'inactive');
    if (!searchTerm.trim()) return byStatus;
    const q = searchTerm.toLowerCase().trim();
    return byStatus.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [teams, activeTab, searchTerm]);

  const isUserPendingActivation = (u) => u.hasCompletedOnboarding === false;
  const isUserActive = (u) => u.isActive !== false && !u.deactivate_account && (u.status || 'active') === 'active';

  // Department admin: active = not deactivated; inactive = deactivated
  const activeMembersCount = members.filter(
    (m) => !m.deactivate_account && m.isActive !== false
  ).length;
  const inactiveMembersCount = members.filter(
    (m) => m.deactivate_account || m.isActive === false
  ).length;
  const memberTabsList = [
    { id: 'active', label: 'Active members', count: activeMembersCount },
    { id: 'inactive', label: 'Inactive members', count: inactiveMembersCount },
  ];

  useLayoutEffect(() => {
    if (!isDepartmentAdmin) return;
    const container = memberTabsRef.current;
    if (!container) return;
    const tabEl = container.querySelector(`[data-tab-id="${memberActiveTab}"]`);
    if (!tabEl) return;
    const measure = () => {
      const left = tabEl.offsetLeft;
      const width = tabEl.offsetWidth;
      setMemberIndicator((prev) =>
        prev.left === left && prev.width === width ? prev : { left, width }
      );
    };
    measure();
    const t1 = setTimeout(measure, 0);
    const t2 = setTimeout(measure, 50);
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      ro.disconnect();
    };
  }, [isDepartmentAdmin, memberActiveTab]);

  const filteredMembers = useMemo(() => {
    const byStatus =
      memberActiveTab === 'active'
        ? members.filter((m) => !m.deactivate_account && m.isActive !== false)
        : members.filter((m) => m.deactivate_account || m.isActive === false);
    if (!memberSearchTerm.trim()) return byStatus;
    const q = memberSearchTerm.toLowerCase().trim();
    return byStatus.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
    );
  }, [members, memberActiveTab, memberSearchTerm]);

  const memberRoleLabel = (m) => {
    if (m.role === 'admin') return 'Org Admin';
    if (m.role === 'department_admin' || m.role === 'dept_admin') return 'Team Admin';
    return 'User';
  };

  const memberRoleOrder = (m) => {
    if (m.role === 'admin') return 0;
    if (m.role === 'department_admin' || m.role === 'dept_admin') return 1;
    return 2;
  };

  const sortedMembersForDisplay = useMemo(() => {
    return [...filteredMembers].sort((a, b) => memberRoleOrder(a) - memberRoleOrder(b));
  }, [filteredMembers]);

  const handleMakeMemberInactive = (member) => {
    setConfirmModal({
      open: true,
      title: 'Make member inactive',
      message: `Make ${member.name || member.email} inactive? They will no longer be able to sign in.`,
      confirmLabel: 'Make Inactive',
      onConfirm: () => {
        setConfirmModal((prev) => ({ ...prev, open: false }));
        usersApi
          .update(member._id, { deactivate_account: true })
          .then(() => {
            setMembers((prev) =>
              prev.map((m) =>
                m._id === member._id ? { ...m, deactivate_account: true, isActive: false } : m
              )
            );
            toast.success('Member set to inactive');
          })
          .catch(() => {
            toast.error('Failed to update member');
          });
      },
    });
  };

  const handleMakeTeamInactive = useCallback(
    (team) => {
      setConfirmModal({
        open: true,
        title: 'Deactivate team',
        message: `Deactivate team "${team.name || 'Untitled team'}"? It will move to Inactive Teams.`,
        confirmLabel: 'Deactivate',
        onConfirm: () => {
          setConfirmModal((prev) => ({ ...prev, open: false }));
          departmentsApi
            .delete(team._id)
            .then(() => {
              toast.success('Team deactivated');
              refreshTeamsAndUsers();
              fetchStats();
            })
            .catch((err) => {
              toast.error(err.response?.data?.message || 'Failed to deactivate team');
            });
        },
      });
    },
    [refreshTeamsAndUsers]
  );

  const handleMakeUserInactiveFromTeam = useCallback(
    (u) => {
      setConfirmModal({
        open: true,
        title: 'Make user inactive',
        message: `Make ${u.name || u.email} inactive? They will no longer be able to sign in.`,
        confirmLabel: 'Make Inactive',
        onConfirm: () => {
          setConfirmModal((prev) => ({ ...prev, open: false }));
          usersApi
            .update(u._id, { deactivate_account: true })
            .then(() => {
              toast.success('User set to inactive');
              refreshTeamsAndUsers();
            })
            .catch(() => {
              toast.error('Failed to update user');
            });
        },
      });
    },
    [refreshTeamsAndUsers]
  );

  const defaultDeptIdsForMember = useMemo(() => {
    const ids = (user?.departmentIds || []).map((d) => (d._id || d).toString());
    return ids.length > 0 ? ids : [];
  }, [user?.departmentIds]);

  const SkeletonBlock = ({ className = '' }) => (
    <div className={`animate-pulse rounded-md bg-gray-200/90 ${className}`} />
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/50"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        {/* Header - same as Job Posts */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Teams
            </h1>
            <p className="mt-1.5 text-gray-600 text-sm sm:text-base">
              Manage your teams, invite members, and track activity in one place.
            </p>
          </div>
          {isOrgAdmin && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setDepartmentDialogOpen(true)}
              data-testid="add-team-btn"
              className="px-5 py-2.5 bg-[#2563eb] text-white rounded-lg hover:bg-[#1d4ed8] flex items-center gap-2 font-semibold shadow-md hover:shadow-lg transition-all duration-200 text-sm shrink-0 w-full sm:w-auto justify-center"
            >
              <Plus className="w-5 h-5" />
              <span>Add Team</span>
            </motion.button>
          )}
          {isDepartmentAdmin && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setEditingMember(null);
                setUserDialogOpen(true);
              }}
              className="px-5 py-2.5 bg-[#2563eb] text-white rounded-lg hover:bg-[#1d4ed8] flex items-center gap-2 font-semibold shadow-md hover:shadow-lg transition-all duration-200 text-sm shrink-0 w-full sm:w-auto justify-center"
            >
              <Plus className="w-5 h-5" />
              <span>Add member</span>
            </motion.button>
          )}
        </div>

        {/* Department admin: members view (same UI pattern as teams) */}
        {isDepartmentAdmin && (
          <>
                <div className="mb-4" ref={memberTabsRef}>
                  <div className="relative flex items-center gap-8 border-b border-gray-200 min-w-0">
                    {memberTabsList.map((tab) => (
                      <button
                        key={tab.id}
                        data-tab-id={tab.id}
                        type="button"
                        onClick={() => setMemberActiveTab(tab.id)}
                        className={`relative z-10 pb-3 pt-0.5 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                          memberActiveTab === tab.id
                            ? 'text-[#2563eb]'
                            : 'text-gray-500 hover:text-gray-900'
                        }`}
                      >
                        {tab.label}
                        <span
                          className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                            memberActiveTab === tab.id
                              ? 'bg-[#2563eb]/10 text-[#2563eb]'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {membersLoading ? <SkeletonBlock className="h-3 w-5 rounded-full" /> : tab.count}
                        </span>
                      </button>
                    ))}
                    <motion.div
                      className="absolute bottom-0 left-0 h-0.5 bg-[#2563eb] z-0"
                      initial={false}
                      animate={{
                        left: memberIndicator.left,
                        width:
                          memberIndicator.width > 0
                            ? memberIndicator.width
                            : memberActiveTab === 'active'
                              ? 140
                              : 160,
                      }}
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      style={{ willChange: 'left, width' }}
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search members by name..."
                      value={memberSearchTerm}
                      onChange={(e) => setMemberSearchTerm(e.target.value)}
                      disabled={membersLoading}
                      className="w-full pl-12 py-3 bg-white border border-gray-300 rounded-lg shadow-sm text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb] hover:border-gray-400 transition-colors pr-4"
                    />
                    {memberSearchTerm && !membersLoading && (
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                        {filteredMembers.length} result{filteredMembers.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <h2 className="text-base font-semibold text-gray-900">All members</h2>
                      <span className="text-sm text-gray-500">
                        {membersLoading ? (
                          <SkeletonBlock className="h-4 w-20" />
                        ) : (
                          `${filteredMembers.length} member${filteredMembers.length !== 1 ? 's' : ''}`
                        )}
                      </span>
                    </div>
                  </div>

                  {membersLoading ? (
                    <div className="divide-y divide-gray-100">
                      {Array.from({ length: 4 }).map((_, idx) => (
                        <div
                          key={`member-skeleton-${idx}`}
                          className="flex items-center justify-between gap-3 px-6 py-4"
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <SkeletonBlock className="h-10 w-10 rounded-lg" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <SkeletonBlock className="h-4 w-36" />
                                <SkeletonBlock className="h-5 w-14 rounded-full" />
                                <SkeletonBlock className="h-5 w-16 rounded-full" />
                              </div>
                              <SkeletonBlock className="h-3 w-52 max-w-full" />
                            </div>
                          </div>
                          <SkeletonBlock className="h-8 w-8 rounded-lg" />
                        </div>
                      ))}
                    </div>
                  ) : filteredMembers.length === 0 ? (
                    <div className="text-center py-16 px-6">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-sm">
                        {memberActiveTab === 'active'
                          ? 'No active members yet. Add a member to get started.'
                          : 'No inactive members.'}
                      </p>
                      {memberActiveTab === 'active' && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingMember(null);
                            setUserDialogOpen(true);
                          }}
                          className="mt-4 px-4 py-2 bg-[#2563eb] text-white rounded-lg hover:bg-[#1d4ed8] text-sm font-medium"
                        >
                          Add member
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {sortedMembersForDisplay.map((member) => {
                        const isActive =
                          !member.deactivate_account && member.isActive !== false;
                        const isOrgAdminRole = member.role === 'admin';
                        const showMakeInactive =
                          !isOrgAdminRole && isActive;
                        const roleLabel = memberRoleLabel(member);
                        return (
                          <div
                            key={member._id}
                            className="flex items-center justify-between gap-3 px-6 py-4 hover:bg-gray-50/50 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className="h-10 w-10 rounded-lg bg-[#2563eb]/10 flex items-center justify-center text-[#2563eb]">
                                <Users className="w-5 h-5" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {isOrgAdminRole ? (
                                    <h3 className="text-sm font-semibold text-gray-900">
                                      {member.email}
                                    </h3>
                                  ) : (
                                    <h3 className="text-sm font-semibold text-gray-900">
                                      {member.name || member.email || '—'}
                                    </h3>
                                  )}
                                  <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                      isActive
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                                    }`}
                                  >
                                    {isActive ? 'Active' : 'Inactive'}
                                  </span>
                                  <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                      roleLabel === 'Org Admin'
                                        ? 'bg-slate-100 text-slate-700 border border-slate-200'
                                        : roleLabel === 'Team Admin'
                                          ? 'bg-violet-100 text-violet-700 border border-violet-200'
                                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                                    }`}
                                  >
                                    {roleLabel}
                                  </span>
                                </div>
                                {!isOrgAdminRole && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {member.email}
                                  </p>
                                )}
                              </div>
                            </div>
                            {showMakeInactive && (
                              <button
                                type="button"
                                onClick={() => handleMakeMemberInactive(member)}
                                className="inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 transition"
                                aria-label="Make inactive"
                                title="Make inactive"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
          </>
        )}

        {/* Org admin: teams view */}
        {isOrgAdmin && (
          <>
            {/* Metric cards (Teams, Users, Assessments, Job Postings, AI Interviews) — commented out for now */}
            {false && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                {metricCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <Link
                      key={card.label}
                      to={card.href}
                      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-gray-300 transition group"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                            {card.label}
                          </p>
                          <p className="text-xl font-bold text-gray-900 mt-0.5 tabular-nums">
                            {card.value}
                          </p>
                        </div>
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-lg ${card.iconBg} ${card.text}`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                      </div>
                      <span className="mt-2 inline-flex items-center text-xs font-medium text-[#2563eb] group-hover:text-[#1d4ed8]">
                        View
                        <ChevronRight className="ml-0.5 w-3.5 h-3.5" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Tabs - Lined style with sliding underline */}
            <div className="mb-4">
              <div
                ref={tabsRef}
                className="relative flex items-center gap-8 border-b border-gray-200 min-w-0"
              >
                {tabsList.map((tab) => (
                  <button
                    key={tab.id}
                    data-tab-id={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative z-10 pb-3 pt-0.5 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                      activeTab === tab.id ? 'text-[#2563eb]' : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                        activeTab === tab.id
                          ? 'bg-[#2563eb]/10 text-[#2563eb]'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {loading ? <SkeletonBlock className="h-3 w-5 rounded-full" /> : tab.count}
                    </span>
                  </button>
                ))}
                <motion.div
                  className="absolute bottom-0 left-0 h-0.5 bg-[#2563eb] z-0"
                  initial={false}
                  animate={{
                    left: indicator.left,
                    width:
                      indicator.width > 0 ? indicator.width : activeTab === 'active' ? 140 : 160,
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 380,
                    damping: 30,
                  }}
                  style={{ willChange: 'left, width' }}
                />
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search teams by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={loading}
                  className="w-full pl-12 py-3 bg-white border border-gray-300 rounded-lg shadow-sm text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 focus:border-[#2563eb] hover:border-gray-400 transition-colors pr-4"
                />
                {searchTerm && !loading && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                    {filteredTeams.length} result{filteredTeams.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Teams list with chips */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-gray-900">All Teams</h2>
                  <span className="text-sm text-gray-500">
                    {loading ? (
                      <SkeletonBlock className="h-4 w-16" />
                    ) : (
                      `${filteredTeams.length} team${filteredTeams.length !== 1 ? 's' : ''}`
                    )}
                  </span>
                </div>
              </div>

              {loading ? (
                <div className="divide-y divide-gray-100">
                  {Array.from({ length: 4 }).map((_, idx) => (
                    <div
                      key={`team-skeleton-${idx}`}
                      className="w-full flex items-center justify-between gap-3 px-6 py-4"
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <SkeletonBlock className="h-10 w-10 rounded-lg" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <SkeletonBlock className="h-4 w-40" />
                            <SkeletonBlock className="h-5 w-14 rounded-full" />
                          </div>
                          <SkeletonBlock className="h-3 w-24" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <SkeletonBlock className="h-8 w-20 rounded-lg" />
                        <SkeletonBlock className="h-8 w-8 rounded-lg" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredTeams.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-sm">
                    {activeTab === 'active'
                      ? 'No active teams yet. Create a team to get started.'
                      : 'No inactive teams.'}
                  </p>
                  {activeTab === 'active' && isOrgAdmin && (
                    <button
                      type="button"
                      onClick={() => setDepartmentDialogOpen(true)}
                      data-testid="add-team-btn"
                      className="mt-4 px-4 py-2 bg-[#2563eb] text-white rounded-lg hover:bg-[#1d4ed8] text-sm font-medium"
                    >
                      Add Team
                    </button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredTeams.map((team) => {
                    const teamId = team._id?.toString() || '';
                    const usersInTeam = (teamUsers[teamId] || []).filter(
                      (u) => (u._id || u.id)?.toString() !== (user?._id || user?.id)?.toString() && !u.deactivate_account && u.isActive !== false
                    );
                    const sortedUsersInTeam = [...usersInTeam].sort((a, b) => {
                      const isAAdmin = a.role === 'department_admin' || a.role === 'dept_admin';
                      const isBAdmin = b.role === 'department_admin' || b.role === 'dept_admin';
                      if (isAAdmin === isBAdmin) return 0;
                      return isAAdmin ? -1 : 1;
                    });
                    const totalMembers = usersInTeam.length + 1;
                    const isOpen = expandedTeams.has(teamId);
                    const lastActiveSummary = getLastActiveTeamMemberSummary(usersInTeam);
                    return (
                      <div key={teamId} className="flex flex-col">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedTeams((prev) => {
                              const next = new Set(prev);
                              if (next.has(teamId)) next.delete(teamId);
                              else next.add(teamId);
                              return next;
                            })
                          }
                          className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-gray-50/50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-lg bg-[#2563eb]/10 flex items-center justify-center text-[#2563eb]">
                              <Building2 className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-sm font-semibold text-gray-900">
                                  {team.name || 'Untitled team'}
                                </h3>
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                    team.status === 'inactive'
                                      ? 'bg-gray-100 text-gray-600 border border-gray-200'
                                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  }`}
                                >
                                  {team.status === 'inactive' ? 'Inactive' : 'Active'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {totalMembers} {totalMembers === 1 ? 'member' : 'members'}
                              </p>
                              {lastActiveSummary && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Last active:{' '}
                                  <span className="text-gray-700">{lastActiveSummary.name}</span>
                                  {' · '}
                                  {lastActiveSummary.relative}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTeamIdForUser(teamId);
                                setNewTeamAdminFlow(false);
                                setUserDialogOpen(true);
                              }}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-[#2563eb] bg-[#2563eb]/5 px-3 py-1.5 text-xs font-medium text-[#2563eb] hover:bg-[#2563eb]/10 transition"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              Add users
                            </button>
                            {team.status !== 'inactive' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMakeTeamInactive(team);
                                }}
                                className="inline-flex items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 transition"
                                aria-label="Make team inactive"
                                title="Make team inactive"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            )}
                            <ChevronDown
                              className={`w-4 h-4 text-gray-400 transition-transform ${
                                isOpen ? 'rotate-180' : 'rotate-0'
                              }`}
                            />
                          </div>
                        </button>
                        <div
                          className={`border-t border-gray-100 overflow-hidden transition-all duration-200 ${
                            isOpen ? 'max-h-[400px]' : 'max-h-0'
                          }`}
                        >
                          <div
                            className={`px-6 pb-4 pt-2 space-y-2 text-xs ${
                              isOpen ? 'opacity-100' : 'opacity-0'
                            }`}
                          >
                            <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 border border-gray-100">
                              <div className="flex flex-col min-w-0">
                                <span className="font-semibold text-gray-900">
                                  {user?.name || user?.email || 'Org Admin'}
                                </span>
                                <span className="text-gray-500">{user?.email}</span>
                              </div>
                              <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-700 shrink-0">
                                Org Admin
                              </span>
                            </div>
                            {sortedUsersInTeam.length === 0 ? (
                              <p className="text-xs text-gray-500 px-1 py-1.5">
                                No users assigned to this team yet.
                              </p>
                            ) : (
                              sortedUsersInTeam.map((u) => {
                                const isOrgAdminUser = u.role === 'admin';
                                const showMakeUserInactive =
                                  !isOrgAdminUser && isUserActive(u);
                                const memberLastLabel = formatLastActive(u.lastActiveAt);
                                return (
                                  <div
                                    key={u._id}
                                    className="flex items-center justify-between rounded-lg px-3 py-2 border border-gray-100 hover:bg-gray-50"
                                  >
                                    <div className="flex flex-col min-w-0">
                                      <span className="font-medium text-gray-900">
                                        {u.name || u.email}
                                      </span>
                                      <span className="text-gray-500">{u.email}</span>
                                      {memberLastLabel && (
                                        <span className="text-gray-400 mt-0.5">
                                          Last active {memberLastLabel}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {isUserPendingActivation(u) && (
                                        <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold border border-amber-200">
                                          Pending activation
                                        </span>
                                      )}
                                      <span
                                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                          isUserActive(u)
                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                                        }`}
                                      >
                                        {isUserActive(u) ? 'Active' : 'Inactive'}
                                      </span>
                                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                        {u.role === 'admin'
                                          ? 'Org Admin'
                                          : u.role === 'department_admin' || u.role === 'dept_admin'
                                            ? 'Team Admin'
                                            : 'User'}
                                      </span>
                                      {showMakeUserInactive && (
                                        <button
                                          type="button"
                                          onClick={() => handleMakeUserInactiveFromTeam(u)}
                                          className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 transition"
                                          aria-label="Make inactive"
                                          title="Make inactive"
                                        >
                                          <UserX className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </>
        )}

        <DepartmentDialog
          open={departmentDialogOpen}
          onClose={() => setDepartmentDialogOpen(false)}
          onSuccess={(dept) => {
            fetchStats();
            if (isOrgAdmin) refreshTeamsAndUsers();
            if (dept && dept._id && !dept.hasManager) {
              setSelectedTeamIdForUser(dept._id.toString());
              setNewTeamAdminFlow(true);
              setUserDialogOpen(true);
            }
          }}
        />
        <UserDialog
          open={userDialogOpen}
          onClose={() => {
            setUserDialogOpen(false);
            setSelectedTeamIdForUser(null);
            setNewTeamAdminFlow(false);
            setEditingMember(null);
          }}
          onSuccess={() => {
            if (isDepartmentAdmin) {
              usersApi.list().then((data) => setMembers(data.users || []));
              setEditingMember(null);
            }
            if (isOrgAdmin) refreshTeamsAndUsers();
            fetchStats();
          }}
          user={editingMember ?? undefined}
          defaultDepartmentIds={
            isDepartmentAdmin
              ? defaultDeptIdsForMember
              : selectedTeamIdForUser
                ? [selectedTeamIdForUser]
                : []
          }
          initialRole={newTeamAdminFlow ? 'department_admin' : 'user'}
        />
        <ConfirmModal
          isOpen={confirmModal.open}
          onClose={() => setConfirmModal((prev) => ({ ...prev, open: false }))}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel="Cancel"
          variant="danger"
        />
      </div>
    </motion.div>
  );
};

export default OrganizationDashboard;
